import { createSelector } from 'reselect';
import { CampaignPurpose } from '../interfaces/campaignPurposes';
import { CampaignStatuses, CampaignType } from '../model/CampaignsModel';
import { formatDisplayName } from '../reducers/campaignPurposes';
import { StoreState } from '../store-interfaces';
import { activeChatbotIdSelector } from './viewSelectors';

const selectCampaignPurposes = (state: StoreState) =>
  (state.campaignPurposes && state.campaignPurposes.purposes) || [];

const selectCampaignPurposeMap = (state: StoreState) =>
  selectCampaignPurposes(state).reduce((obj, item) => {
    obj[item.name] = item.id;
    return obj;
  }, {});

const selectCampaigns = (state: StoreState) => state.campaigns.entities;

export type CampaignTypeWithMappedPurpose = CampaignType & {
  purposeName?: string;
};

function campaignsWithMappedPurpose(
  campaigns: CampaignType[],
  purposes: CampaignPurpose[],
): CampaignTypeWithMappedPurpose[] {
  if (purposes.length) {
    return campaigns.map(campaign => {
      const purposeName = formatDisplayName(campaign.purpose);

      return {
        ...campaign,
        purposeName,
      };
    });
  }

  return campaigns;
}

export type CampaignDisplayStatus =
  | 'Sent'
  | 'Active'
  | 'Ended'
  | 'Draft'
  | 'Delivered'
  | 'Sending'
  | 'Queued'
  | 'Canceled'
  | 'Paused'
  | 'Failed'
  | '';

export type CampaignTypeWithMappedPurposeAndDisplayStatus =
  CampaignTypeWithMappedPurpose & {
    displayStatus: CampaignDisplayStatus;
  };

const formatDripStatus = (status: CampaignStatuses) => {
  switch (status) {
    case 'canceled':
      return 'Ended';
    case 'paused':
      return 'Paused';
    default:
      return 'Active';
  }
};

const formatBlastStatus = (status: CampaignStatuses) => {
  switch (status) {
    case 'canceled':
      return 'Canceled';
    case 'paused':
      return 'Paused';
    case 'failed':
      return 'Failed';
    case 'queued':
    case 'sending':
      return 'Queued';
    case 'sent': // messages cant still be in queue
      return 'Sending';
    case 'delivered':
      return 'Delivered';
  }
};

export function getCampaignDisplayStatus(
  campaign: CampaignType,
): CampaignDisplayStatus {
  const { active, status, scheduleType } = campaign;

  if (!active && status === 'queued') {
    return 'Draft';
  }

  return scheduleType === 'drip'
    ? formatDripStatus(status)
    : formatBlastStatus(status);
}

function campaignsWithDisplayStatus(
  campaigns: CampaignType[],
): CampaignTypeWithMappedPurposeAndDisplayStatus[] {
  return campaigns.map(c => {
    return {
      ...c,
      displayStatus: getCampaignDisplayStatus(c),
    };
  });
}

const hasSendingCampaigns = (campaigns: CampaignType[]): boolean => {
  return campaigns.some(campaign => campaign.status === 'sending');
};

export const campaignsWithMappedPurposeSelector = createSelector(
  [selectCampaigns, selectCampaignPurposes],
  campaignsWithMappedPurpose,
);

export const campaignsWithMappedPurposeAndDisplayStatusSelector =
  createSelector(
    campaignsWithMappedPurposeSelector,
    campaignsWithDisplayStatus,
  );

export const hasSendingCampaignsSelector = createSelector(
  [selectCampaigns],
  hasSendingCampaigns,
);

// The entity we get from the BE cannot be written back to the BE as-is, because:
// `purpose`: is sent as a string, but needs to be written back as a number
// `botId`: is missing
export const updatableCampaignSelector = createSelector(
  [
    campaignsWithMappedPurposeAndDisplayStatusSelector,
    selectCampaignPurposeMap,
    activeChatbotIdSelector,
  ],
  (
    campaigns,
    purposes,
    botId,
  ): CampaignTypeWithMappedPurposeAndDisplayStatus[] =>
    campaigns.map(c => ({
      ...c,
      botId,
      purpose: purposes[formatDisplayName(c.purpose)] || c.purpose,
    })),
);

export const selectDripCampaigns = createSelector(
  [campaignsWithMappedPurposeAndDisplayStatusSelector],
  campaigns => campaigns.filter(campaign => campaign.scheduleType === 'drip'),
);

export const selectChatBlasterCampaigns = createSelector(
  [campaignsWithMappedPurposeAndDisplayStatusSelector],
  campaigns => campaigns.filter(campaign => campaign.scheduleType !== 'drip'),
);

export const hasSendingChatBlastersSelector = createSelector(
  [selectChatBlasterCampaigns],
  hasSendingCampaigns,
);

export const selectCampaignByAudienceId = createSelector(
  [
    updatableCampaignSelector,
    (_, searchAudienceId: number | null) => searchAudienceId,
  ],
  (campaigns, searchAudienceId) =>
    campaigns.find(campaign => {
      const { campaignAudiencesAttributes } = campaign;
      return campaignAudiencesAttributes?.find(
        attributes => attributes.audienceId === searchAudienceId,
      );
    }),
);

export const selectCampaignById = createSelector(
  [updatableCampaignSelector, (_, campaignId: number | null) => campaignId],
  (campaigns, searchCampaignId) =>
    campaigns.find(campaign => campaign.id === searchCampaignId),
);
